import { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { LEGAL } from '../config/legal';

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="rounded-2xl bg-white/10 dark:bg-gray-900/20 shadow-lg border border-white/20 p-6">
      <h2 className="text-xl font-semibold mb-3 text-white">{title}</h2>
      <div className="text-gray-300 space-y-3">{children}</div>
    </section>
  );
}

export default function CGV() {
  const { seller, vat, satisfactionGuaranteeDays, mediator } = LEGAL;

  return (
    <main className="min-h-screen bg-gradient-to-br from-gray-900/80 via-gray-900/60 to-gray-800/80 backdrop-blur-xl flex flex-col items-center p-8 pt-32">
      <div className="max-w-3xl w-full">
        <h1 className="text-4xl font-extrabold mb-8 text-center text-primary-300">
          Conditions générales de vente
        </h1>

        <div className="space-y-6">
          <Section title="1. Vendeur">
            <p>
              Le site mp3randomselector.fr est édité par <strong>{seller.name}</strong>,{' '}
              {seller.status.toLowerCase()}, SIRET {seller.siret}, {seller.address}.
              <br />
              Contact : {seller.email}
            </p>
            <p>
              Les présentes conditions générales de vente (« CGV ») s'appliquent aux ventes de licences
              conclues entre le vendeur et tout client agissant en qualité de consommateur (« le client »).
            </p>
          </Section>

          <Section title="2. Objet et produit">
            <p>
              Les CGV régissent la vente d'une licence d'utilisation du logiciel MP3 Random Selector
              (« la licence »). La licence est personnelle, non exclusive et non transférable.
            </p>
            <p>
              Sa durée (à vie ou à durée limitée) et le nombre maximal de postes sur lesquels elle peut être
              activée sont indiqués sur la page d'achat avant la commande. Une version gratuite du logiciel,
              avec des fonctionnalités limitées, reste disponible sans achat.
            </p>
          </Section>

          <Section title="3. Prix et paiement">
            <p>
              Les prix sont indiqués en euros sur la page d'achat. {vat}. Le prix affiché est le prix à payer.
            </p>
            <p>
              Le paiement s'effectue par carte bancaire, via le prestataire de paiement Stripe, au moment de la
              commande. Le vendeur ne reçoit ni ne conserve les données de la carte du client.
            </p>
            <p>
              Avant de payer, le client coche les cases par lesquelles il déclare avoir lu et accepté les CGV et
              la <Link to="/confidentialite" className="underline text-purple-300">politique de confidentialité</Link>,
              et demande l'exécution immédiate de sa commande (voir l'article 6).
            </p>
          </Section>

          <Section title="4. Livraison">
            <p>
              Dès la confirmation du paiement, la clé de licence est générée et affichée dans l'espace client
              (tableau de bord). Le client active ensuite la licence dans l'application avec l'adresse e-mail de
              son compte et sa clé.
            </p>
          </Section>

          <Section title="5. Garantie commerciale « satisfait ou remboursé »">
            <p>
              Le vendeur offre une garantie commerciale de {satisfactionGuaranteeDays} jours à compter de
              l'achat : si le client n'est pas satisfait, il obtient le remboursement intégral du prix payé.
            </p>
            <p>
              Pour en bénéficier, le client écrit à {seller.email} en indiquant l'adresse e-mail de son compte
              et la référence de son paiement. Le remboursement est effectué sur le moyen de paiement utilisé
              pour la commande, dans les meilleurs délais, et la licence concernée est alors désactivée.
            </p>
          </Section>

          <Section title="6. Droit de rétractation">
            <p>
              Le client dispose en principe d'un délai de 14 jours pour se rétracter d'un achat à distance.
              Toutefois, la licence est un contenu numérique fourni sans support matériel, dont l'exécution
              commence dès la commande : avec l'accord préalable exprès du client et son renoncement exprès à
              son droit de rétractation, ce droit ne peut pas être exercé (art. L221-28 13° du Code de la
              consommation).
            </p>
            <p>
              Cet accord et ce renoncement sont recueillis avant le paiement, par une case à cocher dédiée.
              Cette renonciation ne remet pas en cause la garantie commerciale de {satisfactionGuaranteeDays}{' '}
              jours décrite à l'article 5.
            </p>
          </Section>

          <Section title="7. Garantie légale de conformité">
            <p>
              Indépendamment de la garantie commerciale, le vendeur répond des défauts de conformité de la
              licence, dans les conditions prévues par la loi et sans frais pour le client. Pour toute
              réclamation, écrire à {seller.email}.
            </p>
          </Section>

          <Section title="8. Responsabilité">
            <p>
              Le vendeur met en œuvre les moyens raisonnables pour fournir un logiciel fonctionnel. Le client
              reste responsable des fichiers qu'il copie avec le logiciel et du droit de les utiliser.
            </p>
            <p>
              Ces stipulations ne limitent pas les droits que le client tient de la loi.
            </p>
          </Section>

          {mediator && (
            <Section title="9. Médiateur de la consommation">
              <p>
                En cas de litige non résolu avec le vendeur, le client peut recourir gratuitement au médiateur
                de la consommation suivant : {mediator.name}, {mediator.address}, {mediator.website}.
              </p>
            </Section>
          )}

          <Section title={mediator ? '10. Droit applicable' : '9. Droit applicable'}>
            <p>
              Les CGV sont soumises au droit français. Les données personnelles sont traitées conformément à la{' '}
              <Link to="/confidentialite" className="underline text-purple-300">politique de confidentialité</Link>.
            </p>
          </Section>
        </div>

        <p className="text-gray-400 text-sm text-center mt-8">
          Version {LEGAL.termsVersion}
        </p>
      </div>
    </main>
  );
}
